import React from "react";
import { motion } from "framer-motion";

const ImpactGrid = () => {
  return (
    <section className="py-16 px-6 max-w-7xl mx-auto text-zinc-800 dark:text-zinc-200">
      {/* Headings */}
      <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 dark:text-white leading-tight">
          Built for the next generation of global impact
        </h2>
        <p className="text-zinc-500 dark:text-zinc-400 text-sm sm:text-base font-light leading-relaxed">
          A unified intelligence ecosystem designed to evolve with modern businesses, translating technical complexity into verified ROI, advanced agent integrations, and clear pathways to market leadership.
        </p>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        
        {/* Card 1: For Startups (width: 2 columns on desktop) */}
        <div className="md:col-span-2 flex flex-col justify-between p-8 bg-zinc-100/80 dark:bg-zinc-900/60 rounded-[32px] border border-zinc-200/40 dark:border-zinc-800/40 hover:shadow-md transition-all duration-300">
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">For Startups</h3>
            <p className="text-sm font-light text-zinc-500 dark:text-zinc-400 leading-relaxed">
              An automated dashboard to sync coursework, map AI-driven product trajectories, and dynamically optimize local cloud compute budgets.
            </p>
          </div>
          <div className="mt-8">
            <a 
              href="/strategy" 
              className="inline-block bg-forest hover:bg-forest-hover text-white text-xs font-semibold px-6 py-3 rounded-xl transition-colors duration-200"
            >
              Learn more
            </a>
          </div>
        </div>

        {/* Card 2: For Developers & Builders (width: 4 columns on desktop) */}
        <div className="md:col-span-4 grid grid-cols-1 sm:grid-cols-5 p-8 bg-zinc-100/80 dark:bg-zinc-900/60 rounded-[32px] border border-zinc-200/40 dark:border-zinc-800/40 hover:shadow-md transition-all duration-300 overflow-hidden relative">
          <div className="sm:col-span-3 flex flex-col justify-between h-full space-y-6">
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">For Developers & Builders</h3>
              <p className="text-sm font-light text-zinc-500 dark:text-zinc-400 leading-relaxed">
                A gamified workspace where engineering teams launch collaborative tracks, fine-tune model parameters, and verify high-impact agent networks.
              </p>
            </div>
            <div className="pt-2">
              <a 
                href="/technology" 
                className="inline-block bg-forest hover:bg-forest-hover text-white text-xs font-semibold px-6 py-3 rounded-xl transition-colors duration-200"
              >
                Get started
              </a>
            </div>
          </div>

          {/* Embedded Mockup Widget (looks like figma screenshot) */}
          <div className="sm:col-span-2 mt-6 sm:mt-0 sm:pl-6 flex items-center justify-center">
            <div className="w-full bg-white dark:bg-zinc-950 rounded-2xl border border-zinc-200/80 dark:border-zinc-800/80 shadow-lg p-4 font-mono text-[10px] text-zinc-500 dark:text-zinc-400 space-y-3">
              <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-800 pb-2">
                <div className="flex items-center space-x-1.5">
                  <span className="w-2.5 h-2.5 rounded bg-forest"></span>
                  <span className="font-bold text-zinc-850 dark:text-zinc-300">Workspace</span>
                </div>
                <span className="text-[8px] bg-zinc-100 dark:bg-zinc-900 px-1 py-0.5 rounded text-zinc-400">active</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1 border-b border-zinc-50 dark:border-zinc-900/40">
                  <span>Llama-3 Agent</span>
                  <span className="text-emerald-500 font-bold">$120.00</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-zinc-50 dark:border-zinc-900/40">
                  <span>Pinecone DB</span>
                  <span className="text-red-500 font-bold">$307.00</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span>GPT-4 API</span>
                  <span className="text-emerald-500 font-bold">$29.99</span>
                </div>
              </div>
              <div className="flex gap-1.5 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 text-[8px]">Design</span>
                <span className="px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 text-[8px]">Product</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: For Managers & Operators (width: 2 columns on desktop) */}
        <div className="md:col-span-2 flex flex-col justify-between p-8 bg-zinc-100/80 dark:bg-zinc-900/60 rounded-[32px] border border-zinc-200/40 dark:border-zinc-800/40 hover:shadow-md transition-all duration-300">
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">For Managers & Operators</h3>
            <p className="text-sm font-light text-zinc-500 dark:text-zinc-400 leading-relaxed">
              A secure control center to initialize student-aligned agent environments, monitor real-time milestone progress, and manage compute billing safely.
            </p>
          </div>
          <div className="mt-8">
            <a 
              href="/strategy" 
              className="inline-block bg-forest hover:bg-forest-hover text-white text-xs font-semibold px-6 py-3 rounded-xl transition-colors duration-200"
            >
              Learn more
            </a>
          </div>
        </div>

        {/* Card 4: For Advisors & Strategists (width: 2 columns on desktop - Dark Card!) */}
        <div className="md:col-span-2 flex flex-col justify-between p-8 bg-zinc-950 text-white rounded-[32px] border border-zinc-850 hover:shadow-lg transition-all duration-300 relative overflow-hidden">
          {/* Abstract Grid Line Overlays */}
          <div className="absolute inset-0 opacity-15 pointer-events-none bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-forest/50 via-zinc-900 to-transparent"></div>
          <div className="absolute bottom-0 right-0 w-48 h-48 opacity-25 pointer-events-none">
            <svg viewBox="0 0 100 100" fill="none" className="w-full h-full text-zinc-700">
              <path d="M0 80 Q 50 100, 100 80 T 200 80" stroke="currentColor" strokeWidth="0.5" />
              <path d="M0 60 Q 50 90, 100 60 T 200 60" stroke="currentColor" strokeWidth="0.5" />
              <path d="M0 40 Q 50 80, 100 40 T 200 40" stroke="currentColor" strokeWidth="0.5" />
            </svg>
          </div>

          <div className="space-y-4 relative z-10">
            <h3 className="text-2xl font-bold text-white">For Advisors</h3>
            <p className="text-sm font-light text-zinc-400 leading-relaxed">
              A premium dashboard for elite alumni, advisors, and professionals to asynchronously review student milestones and guide domestic talent.
            </p>
          </div>
          <div className="mt-8 relative z-10">
            <a 
              href="/about" 
              className="inline-block bg-white hover:bg-zinc-200 text-zinc-950 text-xs font-semibold px-6 py-3 rounded-xl transition-colors duration-200"
            >
              Learn more
            </a>
          </div>
        </div>

        {/* Card 5: For Enterprise (width: 2 columns on desktop) */}
        <div className="md:col-span-2 flex flex-col justify-between p-8 bg-zinc-100/80 dark:bg-zinc-900/60 rounded-[32px] border border-zinc-200/40 dark:border-zinc-800/40 hover:shadow-md transition-all duration-300">
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-white">For Enterprise</h3>
            <p className="text-sm font-light text-zinc-500 dark:text-zinc-400 leading-relaxed">
              Centralized platform analytics for school districts and enterprise managers to track aggregate cohort performance and scale achievement portfolios.
            </p>
          </div>
          <div className="mt-8">
            <a 
              href="/strategy" 
              className="inline-block bg-forest hover:bg-forest-hover text-white text-xs font-semibold px-6 py-3 rounded-xl transition-colors duration-200"
            >
              Learn more
            </a>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ImpactGrid;
